import { useMutation, useIsMutating } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { flatten } from 'lodash-es';
import { useCallback, useContext } from 'react';
import {
  addLineItem,
  removeLineItem,
  updateLineItemQuantity,
} from 'utils/api/cartApis';
import { showFlash } from 'utils/domHelpers';
import { logError } from 'utils/helpers';
import { AxiosErrorResponse } from 'utils/types';
import { LineItem } from 'utils/types/orderTypes';

import CartContext from './Cart.context';

type UseCartOptions = {
  addItemToCart?: {
    onSuccess?: (
      response: AxiosResponse<LineItem>,
    ) => Promise<boolean> | boolean;
    onError?: (error: AxiosErrorResponse) => Promise<boolean> | boolean;
  };
};

export const useCart = (options?: UseCartOptions) => {
  const {
    order,
    openCart,
    closeCart,
    isCartOpen,
    refetchOrder,
    isFetchingOrder,
    cartErrors: errors,
    setCartErrors,
    resetOrder,
  } = useContext(CartContext);

  const {
    mutate: _addItemToCart,
    mutateAsync: _addItemToCartAsync,
    isLoading: isAddItemToCartLoading,
    error: addItemToCartError,
  } = useMutation(addLineItem, {
    onSuccess: async (response) => {
      await refetchOrder?.();

      /* allow custom onSuccess method "preventDefault" */
      const shouldContinue =
        (await options?.addItemToCart?.onSuccess?.(response)) ?? true;
      if (!shouldContinue) return;

      openCart();
    },
    onError: async (error: AxiosErrorResponse) => {
      /* allow custom onError method "preventDefault" */
      const shouldContinue =
        (await options?.addItemToCart?.onError?.(error)) ?? true;
      if (!shouldContinue) return;

      const errors = flatten([error.response?.data?.errors || []]);
      setCartErrors(errors);
      if (error.response?.status === 404) {
        showFlash(
          'alert-error',
          'Failed to locate your previous order. Sorry for the inconvenience!',
        );
        resetOrder();
      } else {
        openCart();
      }
    },
  });

  const addItemToCart = useCallback(
    (lineItem: { variant_id: number; quantity: number }) => {
      if (order?.id) {
        _addItemToCart({
          lineItem,
          orderId: order.id,
        });
      } else {
        showFlash(
          'alert-error',
          'Unable to locate your order. Sorry for the inconvenience, please try again!',
        );
        logError('Unable to add line item to cart due to missing order id', {
          component: 'Cart.hooks.ts',
          method: 'addItemToCart',
        });
      }
    },
    [_addItemToCart, order?.id],
  );

  const addItemToCartAsync = async (lineItem: {
    variant_id: number;
    quantity: number;
  }) => {
    if (order?.id) {
      return await _addItemToCartAsync({
        lineItem,
        orderId: order.id,
      });
    } else {
      showFlash(
        'alert-error',
        'Unable to locate your order. Sorry for the inconvenience, please try again!',
      );
      logError('Unable to add line item to cart due to missing order id', {
        component: 'Cart.hooks.ts',
        method: 'addItemToCartAsync',
      });
    }
  };

  const productQuantity = order?.line_items?.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  /**
   * Change the quantity of a line item in the cart.
   */
  const {
    mutate: _updateLineItemQuantity,
    isLoading: isUpdatingLineItemQuantity,
  } = useMutation(updateLineItemQuantity, {
    mutationKey: ['updateLineItemQuantity'],
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const isMutatingLineItemQuantity = useIsMutating({
    mutationKey: ['updateLineItemQuantity'],
  });

  const changeProductQuantity = (
    lineItem: Pick<LineItem, 'id' | 'variant_id' | 'quantity'>,
  ) =>
    order &&
    lineItem.id &&
    _updateLineItemQuantity({
      orderId: order?.id,
      variantId: lineItem.variant_id,
      quantity: lineItem.quantity,
    });

  /**
   * Remove a line item from the cart.
   */
  const {
    mutate: _removeItemFromCart,
    mutateAsync: _removeItemFromCartAsync,
    isLoading: isRemovingItemFromCart,
  } = useMutation(removeLineItem, {
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const removeProduct = (lineItemId: number) =>
    order &&
    lineItemId &&
    _removeItemFromCart({
      orderId: order?.id,
      lineItemId: lineItemId,
    });

  const removeProductAsync = async (lineItemId: number) => {
    if (order && lineItemId) {
      return await _removeItemFromCartAsync({
        orderId: order.id,
        lineItemId: lineItemId,
      });
    }
  };

  return {
    openCart,
    closeCart,
    isCartOpen,

    order,
    refetchOrder,
    isFetchingOrder,
    resetOrder,
    productQuantity,

    addItemToCart,
    addItemToCartAsync,
    isAddItemToCartLoading,
    addItemToCartError,

    /*
    /*
      - `isUpdatingLineItemQuantity`: Tracks the loading state of the **most recent** mutation initiated within this instance of `useMutation`.
        It only reflects the status of the currently updating line item.

      - `isMutatingLineItemQuantity`: Tracks the loading state of **all active mutations** with the key `'updateLineItemQuantity'`,
        including those triggered by other components or instances of `useMutation`.

      - The combined value ensures that if any line item is being updated across the app, the state reflects an ongoing update.
    */
    isUpdatingLineItemQuantity:
      isUpdatingLineItemQuantity || Boolean(isMutatingLineItemQuantity),
    changeProductQuantity,

    isRemovingItemFromCart,
    removeProduct,
    removeProductAsync,
    /**
     * @deprecated please don't use this
     */
    errors,
  };
};

export default useCart;
